import { useState } from 'react';
import { Button, Spinner } from '@fluentui/react-components';
import type { PlanoEscala } from '@supernova/shared';

/**
 * La pregunta de UNIDAD de un plano DWG/DXF con escala dudosa: el lector no pudo saber si el dibujo
 * está en metros, centímetros o milímetros, y de eso dependen TODAS sus medidas. Se pregunta una sola
 * vez por plano, con solo las opciones plausibles (las trae el lector en `escala.opciones`) y lo que
 * implica cada una en palabras. Hasta que se conteste, la pantalla no muestra cantidades de ese plano.
 *
 * La misma pregunta vive en la ficha de la galería y arriba del visor (coherencia: una sola forma).
 */
export function PreguntaUnidad({
  escala,
  onElegir,
  compacta,
}: {
  escala: PlanoEscala;
  /** Guarda la unidad elegida. Si falla, la pregunta se queda para volver a intentar. */
  onElegir: (unidad: string) => Promise<void>;
  /** En la ficha de la galería: más apretada. */
  compacta?: boolean;
}) {
  const [guardando, setGuardando] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const elegir = async (unidad: string) => {
    setGuardando(unidad);
    setError(false);
    try {
      await onElegir(unidad);
    } catch {
      setError(true);
    } finally {
      setGuardando(null);
    }
  };

  return (
    <div
      role="group"
      aria-label="En qué unidad está dibujado este plano"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        display: 'flex', flexDirection: 'column', gap: compacta ? 6 : 8,
        padding: compacta ? '8px 10px' : '10px 14px', borderRadius: 8,
        border: '1px solid var(--warn)', background: 'var(--warn-weak)', color: 'var(--text-primary)',
        cursor: 'default', textAlign: 'left',
      }}
    >
      <div style={{ fontSize: compacta ? 12.5 : 13.5, fontWeight: 700 }}>¿En qué unidad está dibujado este plano?</div>
      <div style={{ fontSize: compacta ? 11.5 : 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>
        El archivo no lo deja claro y de eso dependen todas las medidas. Hasta que me lo digas no te muestro
        cantidades de este plano. Te lo pregunto una sola vez.
      </div>
      <div style={{ display: 'flex', flexDirection: compacta ? 'column' : 'row', flexWrap: 'wrap', gap: 6 }}>
        {escala.opciones.map((o) => (
          <Button
            key={o.unidad}
            size="small"
            appearance="secondary"
            disabled={guardando != null}
            onClick={() => void elegir(o.unidad)}
            style={{ justifyContent: 'flex-start', textAlign: 'left', height: 'auto', padding: '6px 10px', minWidth: 0 }}
          >
            <span style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-start' }}>
              <span style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {guardando === o.unidad && <Spinner size="extra-tiny" />}
                En {o.nombre}
              </span>
              <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-secondary)', whiteSpace: 'normal' }}>
                {o.consecuencia}
              </span>
            </span>
          </Button>
        ))}
      </div>
      {error && (
        <div style={{ fontSize: 11.5, color: 'var(--bad)' }}>No se pudo guardar. Vuelve a intentarlo.</div>
      )}
    </div>
  );
}
